import clsx from "clsx";
import {
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";

const COLLAPSED_HEIGHT = 88;

type Props = PropsWithChildren<{
  openLabel?: string;
  closeLabel?: string;
}>;

export const ExpandableSection = ({
  children,
  openLabel,
  closeLabel,
}: Props) => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const [naturalHeight, setNaturalHeight] = useState(0);

  // Fires synchronously before the browser paints. The resulting setState calls cause
  // a synchronous re-render that commits height: COLLAPSED_HEIGHT to the DOM in the
  // same pre-paint pass — so the browser's first paint already shows the collapsed
  // state with no visible snap or transition.
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const checkExpandable = () => {
      const contentHeight = content.scrollHeight;
      const hasOverflow = contentHeight > COLLAPSED_HEIGHT;
      setIsExpandable(hasOverflow);
      setNaturalHeight(contentHeight);

      if (!hasOverflow) {
        setIsExpanded(false);
      }
    };

    checkExpandable();

    const observer = new ResizeObserver(checkExpandable);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div
        className="relative mb-2 overflow-hidden"
        style={{
          height: isExpandable
            ? isExpanded
              ? naturalHeight
              : COLLAPSED_HEIGHT
            : undefined,
          transition: "height 400ms ease-in-out",
        }}
      >
        <div ref={contentRef}>{children}</div>
        {isExpandable && (
          <div
            className={clsx(
              "absolute bottom-0 h-15 w-full bg-linear-to-b from-white/0",
              {
                "to-white/100": !isExpanded,
                "to-white/0": isExpanded,
              },
            )}
          />
        )}
      </div>

      {isExpandable && (
        <Button
          variant="text"
          onClick={(e) => {
            setIsExpanded((prev) => !prev);
            e.currentTarget.blur();
          }}
          fullWidth
        >
          {isExpanded
            ? (closeLabel ??
              intl.formatMessage({
                defaultMessage: "See less",
                description:
                  "Toggle label to collapse an expanded content section",
              }))
            : (openLabel ??
              intl.formatMessage({
                defaultMessage: "See more",
                description:
                  "Toggle label to expand a collapsed content section",
              }))}
        </Button>
      )}
    </>
  );
};
